import React, { useState } from 'react';
import { TrendingUp, AlertCircle, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { traderStories } from '../mockData';

const StorytellingSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % traderStories.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + traderStories.length) % traderStories.length);
  };

  const handleSubscribe = () => {
    // Track Subscribe event with Facebook Pixel
    if (window.fbq) {
      window.fbq('track', 'Subscribe', {
        source: 'storytelling_slider',
        value: 1,
        currency: 'USD'
      });
    }
    window.open('https://t.me/sinyalbankentry/12328', '_blank');
  };

  const currentStory = traderStories[currentSlide];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-[#F0F0F0]">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* FOMO Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-[#ED1C24]/10 border-2 border-[#ED1C24] rounded-full px-6 py-3 animate-pulse">
              <AlertCircle className="w-5 h-5 text-[#ED1C24]" />
              <span className="text-[#ED1C24] font-bold text-sm md:text-base">🔥 Ribuan Trader Sudah Bergabung!</span>
            </div>
          </div>

          {/* Slider Container */}
          <div className="relative">
            {/* Main Story Card */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 mb-8 border-2 border-gray-100">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-[#ED1C24] to-[#c91820] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-2xl font-bold">{currentStory.name.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold text-black mb-2">{currentStory.name}, {currentStory.age} tahun</h3>
                  <p className="text-gray-600">{currentStory.occupation} dari {currentStory.location}</p>
                </div>
              </div>

              <div className="space-y-6 text-gray-700 leading-relaxed">
                {currentStory.story.map((paragraph, index) => (
                  <p key={index} className="text-lg md:text-xl">
                    {index === 0 ? (
                      <>
                        "{paragraph.split('MC terus')[0]}
                        {paragraph.includes('MC terus') && (
                          <span className="text-[#ED1C24] font-bold">MC terus</span>
                        )}
                        {paragraph.split('MC terus')[1] || paragraph.split('rugi melulu')[1] || paragraph.split('MC 3 kali')[1] || ''}"
                      </>
                    ) : (
                      `"${paragraph}"`
                    )}
                  </p>
                ))}

                {currentStory.proofImage && (
                  <div className="my-6">
                    <img
                      src={currentStory.proofImage}
                      alt={`Bukti trading ${currentStory.name}`}
                      className="w-full max-w-[250px] sm:max-w-xs md:max-w-sm mx-auto rounded-xl shadow-lg border-2 border-[#ED1C24]"
                    />
                  </div>
                )}

                <div className="bg-[#F0F0F0] rounded-xl p-6 border-l-4 border-[#ED1C24]">
                  <p className="text-base md:text-lg font-semibold text-black mb-2">
                    💰 Hasil setelah pakai Indikator SBE:
                  </p>
                  <ul className="space-y-2">
                    {currentStory.results.map((result, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-[#ED1C24] flex-shrink-0" />
                        <span>{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-base md:text-lg italic">
                  "{currentStory.closing.split('Jangan sia-siain kesempatan ini')[0]}
                  {currentStory.closing.includes('Jangan sia-siain kesempatan ini') && (
                    <span className="text-[#ED1C24] font-bold">Jangan sia-siain kesempatan ini</span>
                  )}
                  {currentStory.closing.split('Jangan sia-siain kesempatan ini')[1] || ''}"
                </p>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <button
                onClick={prevSlide}
                className="bg-[#ED1C24] hover:bg-[#c91820] text-white border-0 w-12 h-12 rounded-full flex items-center justify-center transition-colors shadow-lg"
                aria-label="Previous story"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              
              <div className="flex items-center gap-2">
                {traderStories.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentSlide ? 'bg-[#ED1C24] w-8' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to story ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="bg-[#ED1C24] hover:bg-[#c91820] text-white border-0 w-12 h-12 rounded-full flex items-center justify-center transition-colors shadow-lg"
                aria-label="Next story"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* FOMO CTA */}
            <div className="text-center">
              <div className="bg-gradient-to-r from-[#ED1C24] to-[#c91820] rounded-2xl p-8 text-white shadow-xl">
                <TrendingUp className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Jangan Sampai Nyesel Kayak yang Lain!
                </h3>
                <p className="text-lg mb-6 opacity-90">
                  <span className="font-bold">627+ trader aktif</span> udah mulai profit konsisten. Kapan giliran kalian?
                </p>
                <Button 
                  onClick={handleSubscribe}
                  className="bg-white hover:bg-gray-100 text-[#ED1C24] px-8 py-6 text-lg font-bold rounded-xl transition-all hover:scale-105 shadow-lg w-full sm:w-auto"
                >
                  Saya Mau Profit Konsisten
                </Button>
                <p className="text-sm mt-4 opacity-75">⏰ Promo terbatas! Jangan sampai kehabisan slot</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StorytellingSlider;